import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import * as yup from 'yup';
import { css } from '@emotion/core';
import { FLEX_COLUMN } from '../../../layout/flexBox';
import { SELECT } from '../../../forms/styles/select';
import { btnStyle } from '../../../forms/styles/button';
import { COLORS } from '../../../globalStyle/colors';
import LocalBtn from '../../../localization/LocalBtn';
import { Str } from '../../../localization/translationKeys';
import { INPUT } from '../../../forms/styles/input';
import { useMsgText } from '../../../localization/hooks';
import LocalSelect from '../../../localization/LocalSelect';
import { useDispatch } from 'react-redux';
import { useLoading } from '../../../core/api/hooks';
import { ActionType } from '../../../core/redux/actionType';
import { Rater } from '../../survey/raters/models';
import LocalDropdownOption from '../../../localization/LocalDropdownOption';
import { clearLastSentEmailAction } from '../devPlanAction/clearLastSentEmailAction';
import { TIME } from '../../../language/time';


const DROP_DOWN_OPTION_ID_OTHER = -2;

const VERT_MARGIN = css`
  margin-bottom: 14px;
`;

const styles = {
  root:              [
    FLEX_COLUMN,
    css`
      padding: 14px;
      background-color: ${COLORS.LIGHTEST_GREY};
      border-radius: 0 0 10px 10px;
    `,
  ],
  selectPlaceholder: css`
    color: ${COLORS.FIELD_GREY};
  `,
  levelSelect:       [
    SELECT,
    VERT_MARGIN,
    css`
      border: none;
    `,
  ],
  emailInput:        [
    VERT_MARGIN,
    INPUT(),
    css`
      border: none;
    `,
  ],
  commentsInput:     [
    VERT_MARGIN,
    INPUT(),
    css`
      height: 160px;
      padding: 10px;
      resize: none;
      border: none;
    `,
  ],
  submitBtn:         [
    btnStyle.primary(COLORS.DARK_GREY),
    css`

    `,
  ],
};


type Props = {
  participantId: number;
  projectId: number;
  emailingRequired: boolean;
  raters: Rater[];
  onSent?: (email: string) => void;

  onClickSubmitBtn: (email: string, comment?: string) => void;
  submitBtnMsg: string;

  className?: string;
};


/**
 * EmailDevPlanForm: a component that handles emailing dev plans to reviewers
 *
 * PureComponent: true
 * Redux Connected: false
 */
const EmailDevPlanForm: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const selectPlaceholder  = useMsgText(Str.ChooseOne);
  const emailPlaceholder   = useMsgText(Str.Email);
  const commentPlaceholder = useMsgText(Str.OptionalComments);

  const loading = useLoading(ActionType.SendDevPlanRequest);

  const [selection, setSelection]       = useState<number | undefined>(undefined);
  const [email, setEmail]               = useState<string | undefined>(undefined);
  const [comments, setComments]         = useState<string | undefined>(undefined);
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false);
  const [enableEmail, setEnableEmail]   = useState<boolean>(!props.emailingRequired);

  const onChangeSelection = useCallback(
    event => {
      const raterId = +event.target.value;
      setSelection(raterId);

      if (raterId === DROP_DOWN_OPTION_ID_OTHER) {
        setEnableEmail(true);
        setEmail('');
        return;
      }

      setEnableEmail(false);
      const rater = props.raters.find(r => r.raterId === raterId);
      rater && setEmail(rater.email);
    },
    [props.raters, setSelection],
  );

  const onChangeEmail    = useCallback(event => setEmail(event.target.value), [setEmail]);
  const onChangeComments = useCallback(event => setComments(event.target.value), [setComments]);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (loading || !email) {
        return;
      }

      props.onClickSubmitBtn(email, comments);

      // Clear the sent message after 5 seconds
      window.setTimeout(() => dispatch(clearLastSentEmailAction()), 5 * TIME.S);

      props.onSent?.(email);
    },
    [email, comments, props, loading, dispatch],
  );

  useEffect(
    () => {
      yup
        .string()
        .email()
        .required()
        .validate(email)
        .then(() => setEnableSubmit(true))
        .catch(() => setEnableSubmit(false));
    },
    [email, setEnableSubmit],
  );

  return (
    <form
      onSubmit={onSubmit}
      css={styles.root}
      className={props.className}
    >
      { // Show rater level drop-down when emailing is required
        props.emailingRequired && (
          <LocalSelect value={selection} onChange={onChangeSelection} disabled={loading} css={styles.levelSelect}>
            <option dir='auto' value={0} hidden={true} label={selectPlaceholder} css={styles.selectPlaceholder}>
              {selectPlaceholder}
            </option>
            {
              props.raters.map(rater => <option key={rater.raterId} value={rater.raterId}>{rater.email}</option>)
            }
            <LocalDropdownOption labelKey={Str.EmailOther} value={DROP_DOWN_OPTION_ID_OTHER} />
          </LocalSelect>
        )
      }

      <input
        dir='auto'
        value={email}
        onChange={onChangeEmail}
        placeholder={emailPlaceholder}
        disabled={!enableEmail || loading}
        css={styles.emailInput}
      />

      <textarea
        dir='auto'
        value={comments}
        onChange={onChangeComments}
        placeholder={commentPlaceholder}
        disabled={loading}
        css={styles.commentsInput}
      />

      <LocalBtn type='submit' str={props.submitBtnMsg} disabled={!enableSubmit} css={styles.submitBtn} />
    </form>
  );
};

export default EmailDevPlanForm;
